
import React from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Sweepstakes } from '@/hooks/useSweepstakesData';
import BasicInfoSection from './form/BasicInfoSection';
import EligibilityCriteriaSection from './form/EligibilityCriteriaSection';
import PrizeDetailsSection from './form/PrizeDetailsSection';
import FormActions from './form/FormActions';
import ImageUploader from '../ImageUploader';
import { Label } from '@/components/ui/label';

interface SweepstakesFormProps {
  editingSweepstakes: Sweepstakes | null;
  formValues: any;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
  rewards: any[];
}

const SweepstakesForm: React.FC<SweepstakesFormProps> = ({
  editingSweepstakes,
  formValues,
  handleInputChange,
  handleSubmit,
  resetForm,
  rewards
}) => {
  const { t } = useTranslationUtils();
  
  const handleTierChange = (value: string) => {
    formValues.eligibility_criteria.min_tier = value;
  };

  const handleRewardChange = (value: string) => {
    formValues.prize_details.reward_id = value === "none" ? null : value;
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (name.startsWith('eligibility_')) {
      const field = name.replace('eligibility_', '');
      formValues.eligibility_criteria[field] = checked;
    } else {
      formValues[name] = checked;
    }
  };
  
  const handleImageUpload = (url: string) => {
    formValues.image_url = url;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-xl font-bold mb-4">
          {editingSweepstakes 
            ? t('admin.editSweepstakes', 'Edit Sweepstakes') 
            : t('admin.createSweepstakes', 'Create Sweepstakes')}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          <BasicInfoSection 
            formValues={formValues} 
            handleInputChange={handleInputChange} 
          />
          
          <div className="space-y-4">
            <h4 className="text-md font-medium">{t('admin.sweepstakesImage', 'Sweepstakes Image')}</h4>
            <Label>{t('admin.uploadImage', 'Upload Image')}</Label>
            <ImageUploader
              existingUrl={formValues.image_url}
              onUpload={handleImageUpload}
              accept="image/*"
              bucketName="events"
            />
          </div>
          
          <EligibilityCriteriaSection 
            formValues={formValues} 
            handleInputChange={handleInputChange}
            handleTierChange={handleTierChange}
            handleCheckboxChange={handleCheckboxChange}
          />
          
          <PrizeDetailsSection 
            formValues={formValues} 
            handleInputChange={handleInputChange}
            handleRewardChange={handleRewardChange}
            rewards={rewards}
          />
          
          <FormActions 
            editingSweepstakes={editingSweepstakes} 
            resetForm={resetForm} 
          />
        </form>
      </CardContent>
    </Card>
  );
};

export default SweepstakesForm;
